import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';

const CardContainer = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.surface} 0%, ${props => props.theme.colors.divider} 100%);
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 2px solid transparent;
  position: relative;
  cursor: pointer;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);

  &:hover {
    transform: translateY(-8px) rotateX(5deg) rotateY(5deg);
    box-shadow:
      0 20px 25px -5px rgba(0, 0, 0, 0.15),
      0 10px 10px -5px rgba(0, 0, 0, 0.1),
      0 0 0 1px ${props => props.theme.colors.primary}40;
    border-color: ${props => props.theme.colors.primary};
  }

  &:active {
    transform: translateY(-4px) rotateX(2deg) rotateY(2deg);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}20 0%, ${props => props.theme.colors.primary}10 100%);
`;

const CategoryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;

  ${CardContainer}:hover & {
    transform: scale(1.1);
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    ${props => props.theme.colors.primary}40 0%,
    ${props => props.theme.colors.primary}20 50%,
    transparent 100%
  );
  transition: opacity 0.3s ease;

  ${CardContainer}:hover & {
    opacity: 0.8;
  }
`;

const ContentContainer = styled.div`
  padding: 2rem;
  text-align: center;
`;

const CategoryTitle = styled.h3`
  color: ${props => props.theme.colors.heading};
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  transition: color 0.3s ease;
  position: relative;

  ${CardContainer}:hover & {
    color: ${props => props.theme.colors.primary};
  }
`;

const CategoryDescription = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  transition: color 0.3s ease;
`;

const ViewButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryDark} 100%);
  color: white;
  padding: 0.875rem 2rem;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px ${props => props.theme.colors.primary}40;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px ${props => props.theme.colors.primary}60;
    color: white;
  }

  &:hover::before {
    left: 100%;
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: 1.1rem;
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(4px);
  }
`;

const CourseCount = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  box-shadow: 0 2px 8px ${props => props.theme.colors.primary}40;
  z-index: 2;
`;

const CategoryCard = ({ category, courseCount = 0 }) => {
  // Use category image if available, otherwise use mapped images
  const getCategoryImage = (category) => {
    if (category.image) {
      return category.image;
    }

    const imageMap = {
      'Horticulture & Agriculture': '/category-horticulture.jpg',
      'Food, Baking & Meat': '/category-food.jpg',
      'Automotive & Engineering': '/category-automotive.jpg',
      'Construction & Building': '/category-construction.jpg',
      'Business, Management & HR': '/category-business.jpg',
      'Community Services, Health & Education': '/category-community.jpg',
      'Finance & Administration': '/category-finance.jpg',
      'Furniture, Cabinet Making & Joinery': '/category-furniture.jpg',
      'Civil Construction & Plant Operations': '/category-civil.jpg',
      'Beauty, Hair & Personal Services': '/category-beauty.jpg',
      'Hospitality, Cookery & Patisserie': '/category-hospitality.jpg'
    };
    return imageMap[category.name] || '/category-business.jpg';
  };

  return (
    <CardContainer color={category.color}>
      <CourseCount color={category.color}>
        {courseCount} {courseCount === 1 ? 'Course' : 'Courses'}
      </CourseCount>

      <ImageContainer color={category.color}>
        <CategoryImage
          src={getCategoryImage(category)}
          alt={category.name}
          loading="lazy"
        />
        <ImageOverlay color={category.color} />
      </ImageContainer>

      <ContentContainer>
        <CategoryTitle color={category.color}>
          {category.name}
        </CategoryTitle>

        <CategoryDescription>
          {category.description}
        </CategoryDescription>

        <ViewButton to={`/category/${category.slug}`} color={category.color}>
          Explore Courses
          <FiArrowRight />
        </ViewButton>
      </ContentContainer>
    </CardContainer>
  );
};

export default CategoryCard; 